<script module lang="ts">
	import type { MarkdownSource } from './types';

	export interface MarkdownProps {
		color: 'black' | 'gray';
		size: 'small' | 'medium' | 'large';
		source: MarkdownSource;
		dataCy: string;
	}
</script>

<script lang="ts">
	import {
		MarkdownComponents,
		MarkdownTokens,
		type MarkdownOptions,
		type Renderers
	} from '@magidoc/plugin-svelte-marked';
	import { setContext } from 'svelte';
	import {
		MarkdownAccordion,
		MarkdownHtmlAsText,
		MarkdownImage,
		MarkdownLink,
		MarkdownSeparator,
		MarkdownTable
	} from './components';
	import { MARKDOWN_SAFETY_CONTEXT, MARKDOWN_STYLE_CONTEXT } from './constants/contexts';
	import type { MarkdownTokenList } from './types';
	import { defaultRenderers } from './utils/defaultRenderers/defaultRenderers';
	import { getComponents } from './utils/getComponents/getComponents';
	import type { MarkdownSafety } from './utils/getMarkdownSafetyContext/getMarkdownSafetyContext';
	import type { MarkdownStyle } from './utils/getMarkdownStyleContext/getMarkdownStyleContext';
	import { parseMarkdownSource } from './utils/parseMarkdownSource/parseMarkdownSource';

	type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

	interface Props {
		/**
		 * Markdown, or the tokens lexMarkdown made of it on the server: tokens
		 * render without the parser, which a tokens-only build leaves out.
		 */
		source: MarkdownSource;
		dataCy: string;
		color?: 'black' | 'gray';
		size?: 'small' | 'medium' | 'large';
		/**
		 * The element to render as the root. Defaults to `div`. When a heading
		 * (`h1`–`h6`) or `span` is passed, the top-level paragraph is rendered
		 * inline so the root contains only phrasing content — a `<p>` inside a
		 * heading, a button or a sentence is invalid HTML and would be auto-closed
		 * by the browser. Style the inline content via the `class` prop
		 * (e.g. `[&_strong]:...`).
		 */
		component?: 'div' | 'span' | HeadingElement;
		class?: string | undefined;
		id?: string;
		/**
		 * The source can't be trusted (e.g. model output steered by web pages):
		 * block HTML is shown as text instead of being parsed (inline HTML is
		 * dropped, as it always is), and only http(s) / mailto links and http(s)
		 * images are rendered.
		 */
		untrusted?: boolean;
	}

	let {
		source,
		dataCy,
		color = 'black',
		size = 'large',
		component = 'div',
		untrusted = false,
		...rest
	}: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-markdown`);

	let isInline = $derived(component !== 'div');

	// Set by a workspace whose Markdown all arrives as tokens (markdownTokensOnly).
	// Its browser build has no parseMarkdownSource: a string still reaching the
	// browser is parsed once the parser has loaded.
	const TOKENS_ONLY = import.meta.env.VITE_MARKDOWN_TOKENS_ONLY === 'true';

	let lateTokens = $state<MarkdownTokenList>();

	// What can arrive at runtime, even where the types only allow tokens.
	let input = $derived(source as string | MarkdownTokenList | null | undefined);

	// svelte-ignore state_referenced_locally
	if (import.meta.env.SSR && TOKENS_ONLY && typeof input === 'string') {
		console.warn('Markdown rendered a string on the server:', input.slice(0, 80));
	}

	let tokens = $derived.by((): MarkdownTokenList | undefined => {
		if (typeof input !== 'string') return input ?? undefined;
		if (parseMarkdownSource) return parseMarkdownSource(input);
		return lateTokens;
	});

	$effect(() => {
		if (parseMarkdownSource || typeof input !== 'string') return;

		const pending = input;
		console.error('Markdown received a string in a tokens-only build:', pending.slice(0, 80));
		void import('./utils/lexMarkdown/lexMarkdown').then((module) => {
			if (input === pending) lateTokens = module.lexMarkdown(pending);
		});
	});

	const options = { baseUrl: '/' } as unknown as MarkdownOptions;

	let renderers = $derived({
		...defaultRenderers,
		table: MarkdownTable,
		hr: MarkdownSeparator,
		image: MarkdownImage,
		accordion: MarkdownAccordion,
		...getComponents({ color, size }),
		...(isInline ? { paragraph: MarkdownComponents.MarkdownText } : {}),
		// The plugin's own link renderer (used by e.g. accordion bodies)
		// doesn't check hrefs, so untrusted sources use the guarded one.
		...(untrusted ? { html: MarkdownHtmlAsText, link: MarkdownLink } : {})
	} as unknown as Renderers);

	setContext<MarkdownSafety>(MARKDOWN_SAFETY_CONTEXT, {
		get untrusted() {
			return untrusted;
		}
	});

	// Renderers only receive tokens; the accordion label styles itself from
	// the size and colour given here.
	setContext<MarkdownStyle>(MARKDOWN_STYLE_CONTEXT, {
		get size() {
			return size;
		},
		get color() {
			return color;
		}
	});
</script>

<svelte:element this={component} data-cy={dataCyPrefix} {...rest}>
	{#if tokens}
		<MarkdownTokens {tokens} {renderers} {options} />
	{/if}
</svelte:element>
